/**
 * Control Panel WebSocket – connect to relay, send control messages, handle device status.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { BASE_URL } from '../api/client';

function getWsUrl(): string {
  // Prefer explicit API URL so Control Panel WebSocket connects to the same backend (proxy can drop WS upgrades)
  if (process.env.REACT_APP_WS_URL) {
    const base = process.env.REACT_APP_WS_URL.replace(/^http/, 'ws');
    return `${base.replace(/\/$/, '')}/ws`;
  }
  if (process.env.REACT_APP_API_URL) {
    const base = process.env.REACT_APP_API_URL.replace(/^http/, 'ws');
    return `${base.replace(/\/$/, '')}/ws`;
  }
  // In development without env: use same origin so CRA proxy can forward
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    const base = window.location.origin.replace(/^http/, 'ws');
    return `${base.replace(/\/$/, '')}/ws`;
  }
  const base = BASE_URL.replace(/^http/, 'ws');
  return `${base.replace(/\/$/, '')}/ws`;
}

export interface UseControlPanelWsResult {
  /** True only when WebSocket is open and ready. */
  connected: boolean;
  /** True when a connection attempt is in progress (socket not open yet). */
  connecting: boolean;
  connectedDeviceIds: Set<string>;
  send: (message: object) => void;
  requestDeviceList: () => void;
  error: string | null;
}

/**
 * Connect to WebSocket relay as control-panel for the given project.
 * Reconnects on disconnect. Tracks DEVICE_CONNECTED / DEVICE_DISCONNECTED for UI.
 */
export function useControlPanelWs(projectId: string | null): UseControlPanelWsResult {
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectedDeviceIds, setConnectedDeviceIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deviceListIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const projectIdRef = useRef(projectId);

  projectIdRef.current = projectId;

  const connect = useCallback(() => {
    if (!projectIdRef.current) return;
    setConnected(false);
    setConnecting(true);
    setError(null);
    const wsBase = getWsUrl();
    const url = `${wsBase}?projectId=${encodeURIComponent(projectIdRef.current)}&clientType=control-panel`;
    if (process.env.NODE_ENV === 'development') {
      console.log('[ControlPanel WS] Connecting to', url.replace(/\?.*/, ''));
    }
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setConnecting(false);
      setError(msg.includes('insecure') || msg.includes('HTTPS') ? 'WebSocket blocked: page is HTTPS. Use http scheme (androidScheme) or wss:// backend.' : msg);
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      setConnecting(false);
      const pid = projectIdRef.current;
      if (pid) {
        ws.send(JSON.stringify({ type: 'REQUEST_DEVICE_LIST', projectId: pid }));
      }
      if (deviceListIntervalRef.current) clearInterval(deviceListIntervalRef.current);
      deviceListIntervalRef.current = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN && projectIdRef.current) {
          wsRef.current.send(
            JSON.stringify({ type: 'REQUEST_DEVICE_LIST', projectId: projectIdRef.current })
          );
        }
      }, 5000);
      if (process.env.NODE_ENV === 'development') {
        console.log('[ControlPanel WS] Connected', { projectId: projectIdRef.current });
      }
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data as string) as {
          type?: string;
          deviceId?: string;
          deviceIds?: string[];
        };
        const type = data.type;
        const deviceId = typeof data.deviceId === 'string' ? data.deviceId : '';
        if (type === 'DEVICE_LIST' && Array.isArray(data.deviceIds)) {
          setConnectedDeviceIds(new Set(data.deviceIds));
          if (process.env.NODE_ENV === 'development') {
            console.log('[ControlPanel WS] DEVICE_LIST received', { deviceIds: data.deviceIds });
          }
        } else if (type === 'DEVICE_CONNECTED' && deviceId) {
          setConnectedDeviceIds((prev) => new Set(prev).add(deviceId));
        } else if (type === 'DEVICE_DISCONNECTED' && deviceId) {
          setConnectedDeviceIds((prev) => {
            const next = new Set(prev);
            next.delete(deviceId);
            return next;
          });
        }
      } catch {
        // ignore parse errors
      }
    };

    ws.onclose = () => {
      setConnected(false);
      setConnecting(false);
      wsRef.current = null;
      if (deviceListIntervalRef.current) {
        clearInterval(deviceListIntervalRef.current);
        deviceListIntervalRef.current = null;
      }
      if (projectIdRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => connect(), 2000);
      }
    };

    ws.onerror = () => {
      setError('WebSocket error');
      setConnecting(false);
    };
  }, []);

  useEffect(() => {
    if (projectId) {
      connect();
    } else {
      setConnected(false);
      setConnecting(false);
      setConnectedDeviceIds(new Set());
      setError(null);
    }
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (deviceListIntervalRef.current) {
        clearInterval(deviceListIntervalRef.current);
        deviceListIntervalRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setConnected(false);
      setConnecting(false);
    };
  }, [projectId, connect]);

  const send = useCallback(
    (message: object) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      const withTimestamp = { ...message, timestamp: Date.now() };
      ws.send(JSON.stringify(withTimestamp));
    },
    []
  );

  const requestDeviceList = useCallback(() => {
    const ws = wsRef.current;
    const pid = projectIdRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && pid) {
      ws.send(JSON.stringify({ type: 'REQUEST_DEVICE_LIST', projectId: pid }));
    }
  }, []);

  return { connected, connecting, connectedDeviceIds, send, requestDeviceList, error };
}
